import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import formatOrderId from '../../utils/formatOrderId';

const { FiSmartphone, FiCopy, FiCheckCircle, FiRefreshCw } = FiIcons;

const PaymentQRCode = ({ order, onPaymentSuccess }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [upiId] = useState('pashaxerox@axl');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    generateQRCode();
    // Best-effort only; avoid DB writes that may fail on differing schemas
    // storePaymentDetails();
  }, [order]);

  const generateQRCode = async () => {
    try {
      setLoading(true);
      // UPI payment URL format with amount and note
      const upiUrl = `upi://pay?pa=${upiId}&pn=How2Apply&am=${order.totalAmount}&cu=INR&tn=Payment for Order ${order.orderId}`;
      // Generate QR image using QR Server API
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=png&data=${encodeURIComponent(upiUrl)}`;
      setQrCodeUrl(qrApiUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  // Removed DB write for payment pending state to avoid schema mismatches

  const copyUpiId = async () => {
    try {
      await navigator.clipboard.writeText(upiId);
      setCopied(true);
      toast.success('UPI ID copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy UPI ID');
    }
  };

  const copyAmount = async () => {
    try {
      await navigator.clipboard.writeText(order.totalAmount.toString());
      toast.success('Amount copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy amount');
    }
  };

  return (
    <div className="space-y-6">
      {/* QR Code Section */}
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Scan QR Code to Pay</h3>
        
        {loading ? (
          <div className="flex justify-center items-center h-80">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300 inline-block"
          >
            <img 
              src={qrCodeUrl} 
              alt="Payment QR Code" 
              className="w-64 h-64 mx-auto"
              onError={() => {
                toast.error('Failed to load QR code');
                setLoading(false);
              }}
            />
            <p className="text-sm text-gray-600 mt-4">
              Scan with any UPI app to pay ₹{order.totalAmount}
            </p>
          </motion.div>
        )}
      </div>

      {/* Manual Payment Details */}
      <Card className="p-4 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
          <SafeIcon icon={FiSmartphone} className="w-5 h-5 mr-2" />
          Manual Payment Details
        </h4>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">UPI ID:</span>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900">{upiId}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={copyUpiId}
                className="flex items-center space-x-1"
              >
                <SafeIcon icon={copied ? FiCheckCircle : FiCopy} className="w-4 h-4" />
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </Button>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Amount:</span>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900">₹{order.totalAmount}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={copyAmount}
                className="flex items-center space-x-1"
              >
                <SafeIcon icon={FiCopy} className="w-4 h-4" />
                <span>Copy</span>
              </Button>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Reference:</span>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900 font-mono">{formatOrderId(order.orderId)}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const ref = formatOrderId(order.orderId || '');
                  navigator.clipboard.writeText(ref);
                  toast.success('Reference number copied!');
                }}
                className="h-6 w-6 p-0 flex items-center justify-center"
              >
                <SafeIcon icon={FiCopy} className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Instructions */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <h4 className="font-medium text-gray-900 mb-3">Payment Instructions</h4>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li>Open any UPI app (PhonePe, Google Pay, Paytm, etc.)</li>
          <li>Scan the QR code above or enter the UPI ID manually</li>
          <li>Enter the exact amount: ₹{order.totalAmount}</li>
          <li>Add the reference: <span className="font-mono">{formatOrderId(order.orderId)}</span></li>
          <li>Complete the payment</li>
          <li>Wait for automatic verification (usually takes 1-2 minutes)</li>
        </ol>
      </Card>

      {/* Refresh Button */}
      <div className="text-center">
        <Button
          variant="outline"
          onClick={generateQRCode}
          className="flex items-center space-x-2"
        >
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
          <span>Refresh QR Code</span>
        </Button>
      </div>
    </div>
  );
};

export default PaymentQRCode;